import React from 'react';
import { Shield, Printer, ArrowLeft, Globe, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  const lastUpdated = "February 12, 2026";

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 shadow-xl rounded-[2.5rem] overflow-hidden border border-slate-200 dark:border-slate-800">
        
        {/* Header */}
        <div className="bg-brand px-8 py-8 flex justify-between items-center text-white">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 opacity-90" />
            <div>
              <h1 className="text-2xl font-black tracking-tight">Privacy Protocol</h1>
              <p className="text-2xs font-bold uppercase tracking-widest opacity-70">EverydayOS Data Management</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors sm:hidden"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <button 
              onClick={handlePrint}
              className="hidden sm:flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-widest py-2.5 px-5 rounded-xl transition shadow-sm backdrop-blur-md"
            >
              <Printer className="w-4 h-4" /> Print PDF
            </button>
          </div>
        </div>

        {/* Content Container */}
        <div className="p-10 prose prose-slate dark:prose-invert max-w-none text-slate-600 dark:text-slate-400 leading-relaxed">
          <p className="text-xs font-black text-slate-400 uppercase tracking-tighter mb-10 border-b border-slate-100 dark:border-slate-800 pb-4">
            Last Synchronized: {lastUpdated}
          </p>

          <section className="mb-10">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <span className="text-brand">01.</span> Introduction
            </h2>
            <p className="font-medium">
              <strong>EverydayOS</strong>, operated by <strong>N/A Management Services LTD</strong> ("we," "us," or "our"), is committed to the highest standards of household data privacy. This policy describes our protocols regarding the collection and use of your data, including information accessed via Google API Services.
            </p>
          </section>

          {/* Google Limited Use - Critically important for Verification */}
          <section className="mb-10 bg-indigo-50 dark:bg-indigo-900/10 p-8 rounded-[2rem] border border-indigo-100 dark:border-indigo-900/30">
            <h2 className="text-xl font-black text-indigo-900 dark:text-indigo-300 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5" /> Google API Limited Use Disclosure
            </h2>
            <p className="mb-4 text-sm font-bold text-indigo-800 dark:text-indigo-400">
              EverydayOS's use and transfer to any other app of information received from Google APIs will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="underline decoration-2">Google API Service User Data Policy</a>, including the <strong>Limited Use</strong> requirements.
            </p>
            <ul className="space-y-4 ml-2 mt-6">
              {[
                { label: "No Selling", desc: "We never sell, rent, or lease your Google user data to third-party brokers." },
                { label: "Minimal Transfer", desc: "Data transfer only occurs to provide or improve primary user-facing features." },
                { label: "No Advertising", desc: "Google user data is strictly prohibited from use in ad targeting or personalization." },
                { label: "Restricted Access", desc: "Human access to your data is prohibited without explicit support-level consent." }
              ].map((item, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0" />
                  <p className="text-sm">
                    <span className="font-black text-indigo-900 dark:text-indigo-200 uppercase text-2xs tracking-widest mr-2">{item.label}:</span> 
                    {item.desc}
                  </p>
                </li>
              ))}
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
               <span className="text-brand">02.</span> Data Collection Protocols
            </h2>
            <div className="grid gap-6">
              <div className="p-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">Identity & Contact</p>
                <p className="text-xs">Email addresses and names used for node authentication and household registry.</p>
              </div>
              <div className="p-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">Household Content</p>
                <p className="text-xs">User-generated task logs, grocery inventories, asset maintenance schedules, and shared household notes.</p>
              </div>
              <div className="p-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
                <p className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">Calendar Integration</p>
                <p className="text-xs">We access Google Calendar scopes (<code>calendar.events</code>) to synchronize your shared life. This data is stored securely in our encrypted Firestore cluster.</p>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-black text-slate-900 dark:text-white mb-4 flex items-center gap-2">
               <span className="text-brand">03.</span> The "Total Wipe" Security Protocol
            </h2>
            <p className="mb-4">
              EverydayOS is built on the principle of data sovereignty. When a user deletes their account:
            </p>
            <div className="space-y-3 pl-4 border-l-2 border-slate-200 dark:border-slate-800">
              <p className="text-xs font-bold flex items-center gap-2"><Lock className="w-3 h-3" /> Permanent Auth Removal</p>
              <p className="text-xs font-bold flex items-center gap-2"><Lock className="w-3 h-3" /> Recursive Firestore Deletion</p>
              <p className="text-xs font-bold flex items-center gap-2"><Lock className="w-3 h-3" /> Immediate File Purge from Cloud Storage</p>
            </div>
          </section>

          {/* Footer Contact */}
          <div className="mt-20 pt-10 border-t border-slate-100 dark:border-slate-800 text-center">
            <p className="font-black text-slate-900 dark:text-white uppercase tracking-widest text-sm mb-2">N/A Management Services LTD</p>
            <p className="text-xs font-bold text-slate-400">London, United Kingdom</p>
            <p className="text-xs font-bold mt-4">
              Protocol Support: <a href="mailto:nathanielaniballi@gmail.com" className="text-brand hover:underline">nathanielaniballi@gmail.com</a>
            </p>
            <div className="mt-8 flex justify-center gap-6 grayscale opacity-50">
               <div className="h-8 w-px bg-slate-300 dark:bg-slate-700" />
               <p className="text-2xs font-black uppercase tracking-[0.4em]">EverydayOS 2026</p>
               <div className="h-8 w-px bg-slate-300 dark:bg-slate-700" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;